<script lang="ts" setup>
import InputSuffix from '@/components/InputSuffix.vue'
import projectStore from '@/store/ProjectStore'
import userStore from '@/store/UserStore'
import ButtonSet from '@/components/ButtonSet.vue'
import FormElement from '@/components/FormElement.vue'
import formStore from '@/store/FormStore'
import { ValidatorType } from '@/helper/ValidateDirective'
import { CoolingCalculationMethod, HeatingCalculationMethod } from '@/helper/Types'
import HelpLabel from '@/components/HelpLabel.vue'
import { computed } from 'vue'
import { ModalTypes } from '@/helper/ModalTypes.ts'

const props = defineProps<{
  validator: ValidatorType
}>()

const emit = defineEmits<{
  (e: 'toggleHeatingLoadCalc', calculateHeatingLoad: boolean): void
  (e: 'toggleCoolingLoadCalc', calculateCoolingLoad: boolean): void
}>()

const showEnterCalculateButtons = computed(() => {
  if (!userStore.enableHeatingConsumption && !userStore.enableHeatingThermalEnvelope) {
    return false
  }

  return userStore.enableHeatingLoadCalculation
})

const showCalculationMethodButtons = computed(() => {
  return calculateHeatingLoad.value && userStore.enableHeatingConsumption && userStore.enableHeatingThermalEnvelope
})

const calculateHeatingLoad = computed({
  get() {
    return projectStore.projectProperties.heatingCalculationMethod.val !== HeatingCalculationMethod.MANUAL
  },
  set(isActive) {
    let method = HeatingCalculationMethod.MANUAL
    if (isActive) {
      if (userStore.enableHeatingThermalEnvelope) {
        method = HeatingCalculationMethod.THERMAL_ENVELOPE
      } else if (userStore.enableHeatingConsumption) {
        method = HeatingCalculationMethod.CONSUMPTION
      }
    }
    projectStore.projectProperties.heatingCalculationMethod.val = method
  }
})

const calculateCoolingLoad = computed({
  get() {
    return projectStore.projectProperties.coolingCalculationMethod.val !== CoolingCalculationMethod.MANUAL
  },
  set(isActive) {
    projectStore.projectProperties.coolingCalculationMethod.val = isActive
      ? CoolingCalculationMethod.THERMAL_ENVELOPE
      : CoolingCalculationMethod.MANUAL
  }
})

function calculateHeatingClicked(): void {
  if (formStore.data.coolingCalculationMethod !== CoolingCalculationMethod.THERMAL_ENVELOPE) {
    formStore.data.buildingHull = undefined
  }

  formStore.data.heatingLoss = ''

  if (userStore.enableLivingArea) {
    formStore.data.livingArea = ''
  }

  emit('toggleHeatingLoadCalc', calculateHeatingLoad.value)
}

function heatLossCalculationMethodChanged(): void {
  if (formStore.data.heatingCalculationMethod === HeatingCalculationMethod.CONSUMPTION && formStore.data.coolingCalculationMethod !== CoolingCalculationMethod.THERMAL_ENVELOPE) {
    formStore.data.buildingHull = undefined
  }

  if (formStore.data.heatingCalculationMethod === HeatingCalculationMethod.THERMAL_ENVELOPE) {
    formStore.data.annualConsumption = undefined
    formStore.data.annualConsumptionUnit = undefined
    formStore.data.operatingHours = undefined
    formStore.data.previousFuelType = undefined
  }

  formStore.data.heatingLoss = ''
}

function calculateCoolingClicked(): void {
  formStore.data.coolingLoad = ''
  emit('toggleCoolingLoadCalc', calculateCoolingLoad.value)
}

defineExpose({
  validator: props.validator
})
</script>

<template>
  <section
    v-if="formStore.isCooling || formStore.isHeating"
    class="form__section has--line section--load-calculation row"
  >
    <div v-if="formStore.isHeating" class="form__section-inner section--heat-loss col-md-6">
      <div class="form__title">
        <h2>{{ $t('step_heating_details.title_heat_loss') }}</h2>
        <HelpLabel
          v-if="userStore.enableHeatingLoadCalculationInfo && calculateHeatingLoad"
          :modal-type="ModalTypes.HEATING_CALC_INFO"
        />
      </div>

      <ButtonSet
        v-if="showEnterCalculateButtons"
        key="button-set-calculate-heating-load"
        v-model="calculateHeatingLoad"
        :buttons="[
          {
            caption: $t('step_heating_details.btn_enter_load'),
            value: false
          },
          {
            caption: $t('step_heating_details.btn_calculated_load'),
            value: true
          }
        ]"
        :fill-width="true"
        :has-large-space="true"
        name="calculate-heating-load"
        @change="calculateHeatingClicked"
      />

      <ButtonSet
        v-if="showCalculationMethodButtons"
        key="button-set-heating-load-calculation-method"
        v-model="formStore.data.heatingCalculationMethod"
        :buttons="[
          {
            caption: $t('step_heating_details.btn_thermal_envelope'),
            value: HeatingCalculationMethod.THERMAL_ENVELOPE
          },
          {
            caption: $t('step_heating_details.btn_consumption_method'),
            value: HeatingCalculationMethod.CONSUMPTION
          }
        ]"
        :fill-width="true"
        :has-large-space="true"
        name="heating-load-calculation-method"
        @change="heatLossCalculationMethodChanged"
      />

      <div v-if="!calculateHeatingLoad" class="form__row">
        <FormElement
          v-validate="projectStore.projectProperties.heatingLoss.formValidationRule"
          :error="validator.heatingLoss?.error"
          :external-link-url="userStore.heatLossExternalLinkUrl"
          :required="projectStore.formPropertyIsRequired('heatingLoss')"
          label-key="step_heating_details.heat_loss_amount"
        >
          <InputSuffix
            id="heating-loss"
            key="heating-loss"
            v-model="formStore.data.heatingLoss"
            :placeholder="$t('step_heating_details.placeholder_heat_loss_amount')"
            suffix=" kW"
          />
        </FormElement>

        <FormElement
          v-if="userStore.enableLivingArea"
          v-validate="projectStore.projectProperties.livingArea.formValidationRule"
          :error="validator.livingArea?.error"
          :required="projectStore.formPropertyIsRequired('livingArea')"
          label-key="step_heating_details.heat_loss_living_area"
        >
          <InputSuffix
            id="living-area"
            v-model="formStore.data.livingArea"
            :placeholder="$t('step_heating_details.placeholder_heat_loss_living_area')"
            suffix=" m²"
          />
        </FormElement>
      </div>
    </div>

    <div v-if="formStore.isCooling" class="form__section-inner section--cooling-load col-md-6">
      <div class="form__title">
        <h2>{{ $t('step_heating_details.title_cooling_calculation') }}</h2>
        <HelpLabel
          v-if="userStore.enableCoolingLoadCalculationInfo && calculateCoolingLoad"
          :modal-type="ModalTypes.COOLING_CALC_INFO"
        />
      </div>

      <ButtonSet
        v-if="userStore.enableCoolingLoadCalculation"
        key="button-set-calculate-load"
        v-model="calculateCoolingLoad"
        :buttons="[
          {
            caption: $t('step_heating_details.btn_enter_load'),
            value: false
          },
          {
            caption: $t('step_heating_details.btn_calculated_load'),
            value: true
          }
        ]"
        :fill-width="true"
        :has-large-space="true"
        :name="'calculate-load'"
        @change="calculateCoolingClicked"
      />

      <div v-if="!calculateCoolingLoad" class="form__row">
        <FormElement
          v-validate="projectStore.projectProperties.coolingLoad.formValidationRule"
          :error="validator.coolingLoad?.error"
          :required="projectStore.formPropertyIsRequired('coolingLoad')"
          label-key="step_heating_details.label_cooling_load"
        >
          <InputSuffix
            id="cooling-load"
            key="cooling-load"
            v-model="formStore.data.coolingLoad"
            :placeholder="$t('step_heating_details.placeholder_cooling_load')"
            suffix=" kW"
          />
        </FormElement>
      </div>
    </div>
  </section>
</template>
